import { deleteThingModel, thingModelUnit } from '@/api';
import { addProductPoint, getDataType, getProductPointsList, getRegisterType, updateProductPoint } from '@/api/product';
import useAuthButton from '@/hooks/useAuthButton';
import { PointDataType, PointItem } from '@/types';
import { ExportOutlined, ImportOutlined } from '@ant-design/icons';
import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { EditableFormInstance, EditableProTable } from '@ant-design/pro-components';
import { Button, Card, Input, Popconfirm, Space, Typography, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import DataModal, { DataModalRef } from './DataModal';
import './ModbusDetail.less';
import { ThingModelUnit } from './types';

const { Text, Link } = Typography;

interface DefaultDetailProps {
  productId: string | undefined;
}

interface CascaderOption {
  label: string;
  value: string;
  children?: CascaderOption[];
}

interface PointListParams {
  productId: string;
  page: number;
  pageSize: number;
  name?: string;
}

export const ModbusDetail: React.FC<DefaultDetailProps> = ({ productId }) => {
  const { checkPermissionDisabled } = useAuthButton();
  const [editableKeys, setEditableRowKeys] = useState<React.Key[]>([]);
  const [dataSource, setDataSource] = useState<readonly PointItem[]>([]);
  const [unitOptions, setUnitOptions] = useState<ThingModelUnit[]>([]);
  const [registerTypeOptions, setRegisterTypeOptions] = useState<string[]>([]);
  const [dataTypeOptions, setDataTypeOptions] = useState<PointDataType[]>([]);
  const [cascaderOptions, setCaseCaderOptions] = useState<CascaderOption[]>([]);
  const [accessModelOptions, setAccessModelOptions] = useState<{ label: string; value: string }[]>([
    { label: 'R', value: 'R' },
    { label: 'RW', value: 'RW' },
  ]);
  const [current, setCurrent] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const actionRef = useRef<ActionType>();
  const editableFormRef = useRef<EditableFormInstance>();
  const dataModalRef = useRef<DataModalRef>(null);

  // 处理数据类型选项的函数
  const handleDataTypeOptions = (dataTypeList: PointDataType[]) => {
    setDataTypeOptions(dataTypeList);
    const options: CascaderOption[] = [];
    dataTypeList?.forEach((item: { dataType: string; dataOrders: string[]; symbol: string }) => {
      const option: CascaderOption = {
        label: item.dataType,
        value: item.dataType,
      };
      if (item.dataOrders && item.dataOrders.length > 0) {
        option.children = item.dataOrders.map((order: string) => ({
          label: order,
          value: order,
        }));
      }
      options.push(option);
    });
    setCaseCaderOptions(options);
  };

  useEffect(() => {
    thingModelUnit<ThingModelUnit>({ isAll: true }).then((res) => {
      if (res.success && Array.isArray(res.result.list)) {
        setUnitOptions(res.result.list);
      }
    });

    getRegisterType().then((res) => {
      if (res.success) {
        setRegisterTypeOptions(res.result);
      }
    });

    getDataType().then((res) => {
      if (res.success) {
        handleDataTypeOptions(res.result);
      }
    });
  }, []);

  const handleSaveRow = async (row: PointItem & { id?: string; index?: number }) => {
    const data = { ...row } as any;
    delete data.index;

    const unitId = data.property.specs.unit;
    const unitObj = (unitOptions || []).filter((v) => v.id === unitId)[0];
    if (unitObj) {
      data.property.specs.unit = unitObj.symbol;
      data.property.specs.unitName = unitObj.unit_name;
    }

    data.property.specs.multiplier = data.property.specs.multiplier.toString();
    const dataType = data.property.specs.data_type_arr;
    if (dataType.length) {
      data.property.specs.data_type = dataType[0];
      data.property.specs.data_order = dataType[1];
      const dataTypeItem = (dataTypeOptions || []).filter((v) => v.dataType === dataType[0])[0];
      if (dataTypeItem) {
        data.property.type = dataTypeItem.symbol;
      }

      delete data.property.specs.data_type_arr;
    }

    let res: any;
    if (row.id.includes('add') || !row.id) {
      // 新增
      delete data.id;
      res = await addProductPoint(data);
      if (res.success) {
        // 用接口返回的 id 替换本地 dataSource 里的临时 id
        message.success('保存成功');
        return true;
      } else {
        message.error(res.errorMsg);
        return Promise.reject(res.errorMsg);
      }
    } else {
      // 编辑
      res = await updateProductPoint(data);
      if (res.success) {
        message.success('保存成功');
        return true;
      } else {
        message.error(res.errorMsg);
        return Promise.reject(res.errorMsg);
      }
    }
  };

  const columns: ProColumns<PointItem>[] = [
    {
      title: '点位名称',
      dataIndex: 'name',
      formItemProps: {
        rules: [{ required: true, message: '请输入点位名称' }],
      },
    },
    {
      title: '点位别名',
      dataIndex: 'code',
      formItemProps: {
        rules: [
          { required: true, message: '请输入点位别名' },
          { pattern: /^[A-Za-z]+$/, message: '点位别名只能使用英文' },
        ],
      },
    },
    {
      title: '功能码',
      dataIndex: ['property', 'specs', 'register_type'],
      valueType: 'select',
      fieldProps: () => ({
        options: registerTypeOptions.map((item: string) => ({
          label: item,
          value: item,
        })),
        showSearch: true,
        allowClear: true,
        style: { width: '100%' },
        notFoundContent: '暂无数据',
        onChange: (value: string) => {
          if (value) {
            let newOptions;
            // 更新读写属性选项
            switch (value) {
              case '01 读线圈状态':
                newOptions = [
                  { label: 'R', value: 'R' },
                  { label: 'RW', value: 'RW' },
                ];
                break;
              case '02 读离散输入状态':
              case '04 读输入寄存器':
                newOptions = [{ label: 'R', value: 'R' }];
                break;
              case '03 读保持寄存器':
                newOptions = [
                  { label: 'R', value: 'R' },
                  { label: 'RW', value: 'RW' },
                ];
                break;
              default:
                newOptions = [
                  { label: 'R', value: 'R' },
                  { label: 'RW', value: 'RW' },
                ];
            }
            setAccessModelOptions(newOptions);

            // 清空数据类型和读写属性
            editableKeys.forEach((key) => {
              editableFormRef.current?.setRowData?.(key as string, {
                property: {
                  access_model: undefined,
                  specs: {
                    data_type_arr: undefined,
                  },
                },
              });
            });

            // 获取数据类型
            getDataType({ registerName: value }).then((res) => {
              if (res.success) {
                handleDataTypeOptions(res.result);
              }
            });
          }
        },
      }),
      formItemProps: {
        rules: [{ required: true, message: '请选择功能码' }],
      },
    },
    {
      title: '数据类型',
      dataIndex: ['property', 'specs', 'data_type_arr'],
      valueType: 'cascader',
      fieldProps: {
        options: cascaderOptions,
        changeOnSelect: true,
        style: { width: '100%' },
      },
      formItemProps: {
        rules: [
          { required: true, message: '请选择数据类型' },
          {
            validator: (_, value) => {
              if (!value || !value[0]) return Promise.resolve();
              // 查找选中的选项
              const selectedOption = cascaderOptions.find((opt) => opt.value === value[0]);
              // 如果有子类但没有选择子类
              if (selectedOption?.children?.length && (!value[1] || value[1].length === 0)) {
                return Promise.reject(new Error('请选择具体的数据类型'));
              }
              return Promise.resolve();
            },
          },
        ],
      },
      render: (_, record) => {
        const { data_type, data_order } = record.property.specs;
        if (data_type && data_order) {
          return `${data_type} / ${data_order}`;
        }
        if (data_type) {
          return `${data_type}`;
        }
        return '-';
      },
    },
    {
      title: '小数点位数',
      dataIndex: ['property', 'specs', 'precision'],
      valueType: 'select',
      fieldProps: () => {
        let dataType;
        editableKeys.forEach((key) => {
          const rowData = editableFormRef.current?.getRowData?.(key as string | number);
          dataType = rowData?.property?.specs?.data_type_arr?.[0];
          if (dataType !== 'Float32(4 Bytes)') {
            editableFormRef.current?.setRowData?.(key as string | number, {
              property: {
                specs: {
                  precision: '0',
                },
              },
            });
          }
        });
        return {
          options: [
            { label: '0', value: '0' },
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
          ],
          disabled: dataType !== 'Float32(4 Bytes)',
          style: { width: '100%' },
        };
      },
      initialValue: 1,
    },
    {
      title: '寄存器地址',
      dataIndex: ['property', 'specs', 'register_address'],
      formItemProps: {
        rules: [
          { required: true, message: '请输入寄存器地址' },
          { pattern: /^0$|^[1-9]\d*$/, message: '寄存器地址只能为大于等于0的整数' },
        ],
      },
    },
    {
      title: '读写属性',
      dataIndex: ['property', 'access_model'],
      valueType: 'select',
      fieldProps: {
        options: accessModelOptions,
        style: { width: '100%' },
      },
      formItemProps: {
        rules: [{ required: true, message: '请选择读写属性' }],
      },
    },
    {
      title: '倍率',
      dataIndex: ['property', 'specs', 'multiplier'],
      initialValue: '1',
      formItemProps: {
        rules: [
          { required: true, message: '请输入倍率' },
          { pattern: /^(0*[1-9]\d*(\.\d+)?|0+\.\d*[1-9]\d*)$/, message: '倍率只能为大于0的数值' },
        ],
      },
    },
    {
      title: '单位',
      dataIndex: ['property', 'specs', 'unit'],
      valueType: 'select',
      fieldProps: {
        options: unitOptions.map((item: any) => ({
          label: `${item.unit_name}(${item.symbol})`,
          value: item.id,
        })),
        showSearch: true,
        allowClear: true,
        style: { width: '100%' },
        notFoundContent: '暂无数据',
      },
      render: (_, record) => {
        const { unit, unitName } = record.property.specs;
        if (unit && unitName) {
          return `${unitName}(${unit})`;
        }
        if (unit) {
          return unit;
        }
        return '-';
      },
    },
    {
      title: '操作',
      valueType: 'option',
      width: 150,
      render: (text, record, _, action) => [
        <Link key="copy" onClick={() => {}}>
          复制
        </Link>,
        <Text type="secondary">/</Text>,
        <Link
          key="edit"
          onClick={() => {
            action?.startEditable?.(record.id);
          }}
        >
          编辑
        </Link>,
        <Text type="secondary">/</Text>,
        <Popconfirm
          key="delete"
          title="此操作将永久删除该记录, 是否继续?"
          okText="删除"
          cancelText="取消"
          placement="topRight"
          disabled={checkPermissionDisabled('gateway:product:tsl:remove')}
          onConfirm={() => {
            deleteThingModel({
              thing_model_id: record?.id,
              thing_model_type: record?.thing_model_type,
            }).then((res) => {
              if (res.success) {
                message.success('删除成功');
                actionRef?.current?.reloadAndRest?.();
              } else {
                message.error(res?.errorMsg);
              }
            });
          }}
        >
          <Typography.Link disabled={checkPermissionDisabled('gateway:product:tsl:remove')}>删除</Typography.Link>
        </Popconfirm>,
      ],
    },
  ];

  return (
    <Card className="m20">
      <Space className="mb15 align-center justify-space-between" wrap>
        <Input.Search
          placeholder="请输入点位名称/点位别名搜索"
          style={{ width: 300 }}
          onSearch={(value) => {
            setSearchKeyword(value);
            actionRef.current?.reloadAndRest?.();
          }}
          allowClear
          onClear={() => {
            setSearchKeyword('');
            actionRef.current?.reload();
          }}
        />
        <Space>
          <Button
            type="primary"
            icon={<ImportOutlined />}
            onClick={() => {
              productId && dataModalRef.current?.open(productId, 'import');
            }}
            disabled={checkPermissionDisabled('gateway:product:tsl:import')}
          >
            导入
          </Button>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={() => {
              productId && dataModalRef.current?.open(productId, 'export');
            }}
            disabled={checkPermissionDisabled('gateway:product:tsl:export')}
          >
            导出
          </Button>
        </Space>
      </Space>
      <EditableProTable<PointItem>
        actionRef={actionRef}
        rowKey="id"
        recordCreatorProps={{
          position: 'top',
          record: () => {
            const newId = 'add' + (Math.random() * 1000000).toFixed(0);
            if (current !== 1 && editableKeys.length) {
              actionRef.current?.reloadAndRest?.();
            }
            const item: any = {
              id: newId,
              index: 9,
              thing_model_type: 'property',
              product_id: productId || '',
              name: '',
              code: '',
              property: {
                access_model: undefined,
                require: false,
                type: '',
                specs: {
                  slave_id: '',
                  register_type: undefined,
                  data_type: undefined,
                  data_order: '',
                  register_address: '',
                  multiplier: 1,
                  precision: '0',
                  unit: undefined,
                  unitName: '',
                },
              },
            };

            return item;
          },
          creatorButtonText: '添加点位',
        }}
        loading={false}
        columns={columns}
        value={dataSource}
        onChange={setDataSource}
        request={async (params) => {
          if (!productId || !params.current || !params.pageSize) {
            return { data: [], total: 0, success: false };
          }
          const res = await getProductPointsList({
            productId,
            page: params.current,
            pageSize: params.pageSize,
            name: searchKeyword,
          } as PointListParams);
          if (res.success) {
            // 处理数据，为级联选择器准备数据
            const list = [...res.result.list];
            list.forEach((item) => {
              (item.property.specs as any).data_type_arr = [item.property.specs.data_type];
              if (item.property.specs.data_order) {
                (item.property.specs as any).data_type_arr.push(item.property.specs.data_order);
              }
            });

            return {
              data: list,
              total: res.result.total,
              success: true,
            };
          }
          return {
            data: [],
            total: 0,
            success: false,
          };
        }}
        editable={{
          type: 'multiple',
          editableKeys,
          onSave: async (rowKey, data) => {
            const result = await handleSaveRow(data);
            actionRef?.current?.reload?.();
            return result;
          },
          onChange: setEditableRowKeys,
          actionRender: (row, config, defaultDoms) => {
            return [defaultDoms.save, defaultDoms.cancel];
          },
        }}
        editableFormRef={editableFormRef}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page) => {
            setCurrent(page);
            if (page === 1) return;
            if (editableKeys.length > 0) {
              editableKeys.forEach((key) => {
                actionRef.current?.cancelEditable?.(key);
              });
            }
            setEditableRowKeys([]);
          },
        }}
        style={{ width: '100%' }}
        className="custom-table"
      />
      <DataModal
        ref={dataModalRef}
        onSuccess={() => {
          actionRef?.current?.reloadAndRest?.();
        }}
      />
    </Card>
  );
};
